import { z } from "zod";

export interface GraphCollection<T> {
  value: T[];
  "@odata.nextLink"?: string;
}

export type Folder = {
  name: string;
  contents: (Folder | File)[];
  type: "folder";
  loading?: boolean;
};

export type File = z.infer<typeof fileSchema>;

export interface FileWithAbsolutePath extends File {
  absolutePath: string[];
}

export const sharePointOriginSchema = z.enum([
  "sharepoint-train_operation-documents",
  "sharepoint-train_operation-fido_reserve",
]);

export type SharePointOrigin = z.infer<typeof sharePointOriginSchema>;

export const originSchema = sharePointOriginSchema.or(
  z.enum(["documentdistribution-circulars"]),
);

export type Origin = z.infer<typeof originSchema>;

export const pdfMetaDataSchema = z.object({
  pdfName: z.string().min(1),
  pdfOrigin: originSchema,
  pdfId: z.string().min(1),
  path: z.string().optional(),
});

export const fileSchema = z.object({
  name: z.string().min(1),
  loading: z.boolean().optional(),
  origin: originSchema,
  originId: z.string().min(1),
  webUrl: z.string().url().optional(),
  downloadUrl: z.string().url().optional(),
  expectedFileSize: z.number().optional(),
  icon: z.string().min(1).optional(),
  type: z.literal("file"),
  cacheId: z.string().min(1),
  isFavorite: z.boolean(),
  modifiedAt: z.string().datetime(),
});

export const listItemSchema = z
  .object({
    id: z.string().min(1),
    webUrl: z.string().min(1),
    downloadUrl: z.string().optional(),
    expectedFileSize: z.number().optional(),
    driveItem: z
      .object({
        "@microsoft.graph.downloadUrl": z.string().optional(),
        size: z.number().optional(),
      })
      .optional(),
    fields: z.object({
      Sti: z.string().min(1),
      Selskapskode: z.optional(z.array(z.string().min(1))),
      Mottakere: z.optional(z.array(z.string().min(1))),
      DocIcon: z.string().min(1),
      Title: z.string().min(1),
      ContentType: z.string().min(1),
      Modified: z.string().datetime(),
    }),
  })
  // After parsing, move downloadUrl to top level field and remove driveItem from the object returned from safeParse.
  .transform(({ driveItem, ...data }) => {
    if (driveItem && driveItem["@microsoft.graph.downloadUrl"]) {
      return {
        ...data,
        downloadUrl: driveItem["@microsoft.graph.downloadUrl"],
        expectedFileSize: driveItem.size,
      };
    }
    return data;
  });

export type ListItemWithFields = z.infer<typeof listItemSchema>;
